import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Handshake } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const STATUS_LABELS = {
  demande: { label: "Demandé", emoji: "📨", color: "bg-zinc-800 text-zinc-300 border-zinc-700" },
  repondu: { label: "Répondu", emoji: "💬", color: "bg-blue-950 text-blue-200 border-blue-800" },
  approuve: { label: "Approuvé", emoji: "✅", color: "bg-emerald-950 text-emerald-200 border-emerald-800" },
  actif: { label: "Actif", emoji: "🚀", color: "bg-amber-950 text-amber-200 border-amber-800" },
  refus: { label: "Refus", emoji: "❌", color: "bg-red-950 text-red-200 border-red-800" },
};

const KIND_LABELS = {
  wholesale: "Wholesale",
  dropship: "Dropshipping",
  academy_account: "Academy Account",
  fournisseur_print: "Print-on-demand",
  autre: "Autre",
};

export default function FournisseursB2BModule() {
  const [data, setData] = useState({ suppliers: [], counts: {}, by_status: {}, statuses: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [kit, setKit] = useState(null);
  const [kitTab, setKitTab] = useState("facebook");
  const [broadcastDialog, setBroadcastDialog] = useState(null);
  const [broadcastPreview, setBroadcastPreview] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [showPrep, setShowPrep] = useState(false);
  const [dossier, setDossier] = useState(null); // { supplier, messages, counts }
  const [dossierLoading, setDossierLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const openDossier = async (sup) => {
    setDossier({ supplier: sup, messages: null });
    setAnalysis(sup.last_analysis || null);
    setDossierLoading(true);
    try {
      const r = await api.get(`/b2b/suppliers/${sup.id}/timeline`);
      setDossier({ supplier: sup, messages: r.data.messages || [], counts: r.data.counts || {}, warning: r.data.warning, last_sent: r.data.last_sent || [], last_received: r.data.last_received || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement courriels");
      setDossier({ supplier: sup, messages: [] });
    } finally { setDossierLoading(false); }
  };

  const runAnalysis = async (sup) => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/b2b/suppliers/${sup.id}/analyze`);
      setAnalysis(r.data.analysis);
      toast.success("Analyse IA terminée ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse échouée");
    } finally { setAnalyzing(false); }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/b2b/suppliers");
      setData(r.data);
    } catch (e) {
      toast.error("Erreur chargement fournisseurs");
    } finally {
      setLoading(false);
    }
  };

  const seed = async () => {
    try {
      await api.post("/b2b/suppliers/seed");
      toast.success("Seed des 6 marques effectué");
      reload();
    } catch {
      toast.error("Échec du seed");
    }
  };

  const runRelances = async () => {
    try {
      const r = await api.post("/b2b/relances/run");
      const n = r.data.generated;
      if (n === 0) {
        toast.info("Aucun fournisseur éligible aujourd'hui (tous récents < 7j ou statut fermé)");
      } else {
        toast.success(`${n} brouillon(s) de relance créé(s) dans Gmail > Brouillons`);
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec des relances");
    }
  };

  const relanceOne = async (sid, brand) => {
    if (!window.confirm(`Forcer une relance Gmail pour ${brand} ?`)) return;
    try {
      const r = await api.post(`/b2b/suppliers/${sid}/relance`);
      if (r.data?.ok) {
        toast.success(`Brouillon Gmail créé pour ${brand}`);
        reload();
      } else {
        toast.error(r.data?.error || "Échec");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec relance");
    }
  };

  const openPublicityKit = async (sid, brand) => {
    try {
      const r = await api.get(`/b2b/partners/${sid}/publicity-kit`);
      setKit({ ...r.data, supplier_id: sid });
      setKitTab("facebook");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Partenaire pas encore approuvé/actif");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  const openBroadcastDialog = async (message, supplierBrand, supplierId) => {
    try {
      const r = await api.get("/b2b/broadcast/audience-preview?audience=active_students");
      setBroadcastPreview(r.data);
      setBroadcastDialog({
        message,
        supplierBrand,
        supplierId,
        audience: "active_students",
      });
    } catch (e) {
      toast.error("Impossible de récupérer l'audience");
    }
  };

  const confirmBroadcast = async () => {
    if (!broadcastDialog) return;
    if (!window.confirm(`Envoyer ce SMS à ${broadcastPreview?.total || 0} membres actifs ? Action IRRÉVERSIBLE.`)) return;
    setBroadcasting(true);
    try {
      const r = await api.post("/b2b/broadcast/sms", {
        message: broadcastDialog.message,
        audience: broadcastDialog.audience,
        dry_run: false,
        supplier_id: broadcastDialog.supplierId,
      });
      const d = r.data;
      toast.success(`Broadcast envoyé : ${d.sent} reçus, ${d.failed} échecs, ${d.skipped} skipped`, { duration: 8000 });
      setBroadcastDialog(null);
      setBroadcastPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec broadcast");
    } finally {
      setBroadcasting(false);
    }
  };

  const updateStatus = async (sid, status) => {
    try {
      await api.patch(`/b2b/suppliers/${sid}`, { status });
      toast.success(`Statut → ${STATUS_LABELS[status].label}`);
      reload();
    } catch {
      toast.error("Échec mise à jour");
    }
  };

  const saveEdit = async () => {
    try {
      if (editing.id) {
        await api.patch(`/b2b/suppliers/${editing.id}`, editing);
        toast.success("Fournisseur mis à jour");
      } else {
        await api.post("/b2b/suppliers", editing);
        toast.success("Fournisseur créé");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec sauvegarde");
    }
  };

  const removeSupplier = async (sid) => {
    if (!window.confirm("Supprimer ce fournisseur ?")) return;
    try {
      await api.delete(`/b2b/suppliers/${sid}`);
      toast.success("Supprimé");
      reload();
    } catch {
      toast.error("Échec suppression");
    }
  };

  const [digest, setDigest] = useState(null);

  useEffect(() => { reload(); }, []);
  useEffect(() => {
    api.get("/b2b/digest").then((r) => setDigest(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="fournisseurs-b2b-module">
      {/* Digest quotidien — dossiers qui attendent ta réponse */}
      {digest && digest.count > 0 && (
        <div className="bg-amber-950/30 border border-amber-800/50 rounded-xl p-4" data-testid="b2b-digest-banner">
          <div className="flex items-center gap-2 text-amber-300 font-semibold mb-2">
            📋 {digest.count} dossier(s) attendent TA réponse
          </div>
          <div className="space-y-1.5">
            {digest.awaiting_me.slice(0, 5).map((d) => (
              <button
                key={d.supplier_id}
                onClick={() => { const sup = Object.values(data?.by_status || {}).flat().find((x) => x.id === d.supplier_id); if (sup) openDossier(sup); }}
                className="w-full text-left flex items-center justify-between gap-3 bg-zinc-900/60 hover:bg-zinc-800 rounded-lg px-3 py-2 text-sm transition"
                data-testid={`b2b-digest-item-${d.supplier_id}`}
              >
                <span className="text-zinc-200 font-medium">{d.brand}</span>
                <span className="text-zinc-500 truncate flex-1 mx-2 text-xs">« {d.last_subject || "sans objet"} »</span>
                {d.days_waiting != null && <span className={`text-xs whitespace-nowrap ${d.days_waiting >= 3 ? "text-red-400" : "text-amber-400"}`}>{d.days_waiting} j</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Header */}
      <PageHeader
        title="Fournisseurs B2B"
        icon={Handshake}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Fournisseurs B2B" }]}
        subtitle="Pipeline wholesale / dropshipping / academy accounts du réseau"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setShowPrep(true)}
              data-testid="b2b-prep-button"
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            >
              📋 Préparation rencontre
            </Button>
            <Button
              variant="outline"
              onClick={runRelances}
              data-testid="b2b-run-relances-button"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            >
              ⏰ Lancer relances auto
            </Button>
            <Button
              variant="outline"
              onClick={seed}
              data-testid="b2b-seed-button"
            >
              🌱 Seed (6 marques)
            </Button>
            <Button
              onClick={() => setEditing({ brand: "", kind: "wholesale", status: "demande", disciplines: [], auto_relance_enabled: true })}
              data-testid="b2b-add-button"
              className="bg-amber-500 text-black hover:bg-amber-400"
            >
              + Ajouter fournisseur
            </Button>
          </>
        }
      />

      {/* Préparation rencontre fournisseur */}
      <Dialog open={showPrep} onOpenChange={setShowPrep}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="b2b-prep-dialog">
          <DialogHeader>
            <DialogTitle>📋 Préparation rencontre fournisseur — fais tes devoirs</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 text-sm leading-relaxed">

            <section>
              <h3 className="font-bold text-emerald-400 text-base mb-2">🎤 Mon pitch (30 sec) — qui je suis, le dojo, pourquoi je suis là</h3>
              <p className="bg-zinc-800/50 p-3 rounded border border-zinc-700">
                « Bonjour, je suis Ralph D. Jean-Pierre, fondateur de l'Institut Impact Élite Martial à Val-d'Or, en activité depuis 2010. On a 93 élèves actifs, 7 disciplines, un dojo affilié à Rouyn-Noranda, et on organise 2 compétitions inter-écoles par an (100-200 athlètes). Je bâtis le réseau Kaizen System pour équiper nos membres et les écoles partenaires. Je vous contacte parce que je cherche un partenaire fournisseur fiable et à long terme pour grandir ensemble — pas juste une commande ponctuelle. »
              </p>
            </section>

            <section>
              <h3 className="font-bold text-amber-400 text-base mb-2">❓ Questions qu'ILS pourraient me poser (+ mes réponses prêtes)</h3>
              <div className="space-y-2">
                {[
                  ["Quel est votre volume / nombre de commandes ?", "93 élèves actifs + dojo affilié Rouyn. On estime 80-120 unités/an au départ (gants, gardes, protège-tibias, gis). Objectif 150-300k$ d'ici 3 ans avec le réseau Kaizen."],
                  ["Êtes-vous une école ou un revendeur ?", "Les deux : on équipe nos propres élèves (academy) ET on veut faire du dropship vers nos membres et les écoles invitées à nos compétitions."],
                  ["Vous changez de nom / quelle est l'entité ?", "Même fondateur, mêmes standards depuis 2010. Institut Impact reste 100% actif = Franchise #001. Kaizen System Inc. (en incorporation) devient le partenaire central. Un seul interlocuteur stable."],
                  ["Avez-vous un numéro de taxe / d'entreprise ?", "Oui — Impact Kick-Boxing inc., NEQ 1166967514. Les numéros TPS/TVQ suivent (en confirmation avec mon comptable)."],
                  ["MOQ et conditions de paiement ?", "On préfère démarrer en dropship sans gros MOQ, puis passer en bulk. Idéalement Net 30 — quelles sont vos conditions ?"],
                  ["Délais de livraison ?", "Parfait tant qu'on reçoit avant nos compétitions. Quels sont vos lead times vers le Québec (J9P) ?"],
                  ["Que nous offrez-vous en retour ?", "Logo sur notre page /partenaires, mention à nos 93 élèves + parents, visibilité à nos compétitions régionales (100-200 athlètes), posts FB/IG du réseau."],
                ].map(([q, a], i) => (
                  <div key={i} className="bg-zinc-800/50 p-3 rounded border border-zinc-700">
                    <div className="font-semibold text-zinc-100">Q{i + 1}. {q}</div>
                    <div className="text-zinc-300 mt-1">→ {a}</div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="font-bold text-blue-400 text-base mb-2">🙋 Mes questions à LEUR poser</h3>
              <ol className="list-decimal list-inside space-y-1 bg-zinc-800/50 p-3 rounded border border-zinc-700">
                <li>Accès portail / compte pour passer les commandes dropship</li>
                <li>Grille tarifaire wholesale (+ rabais par volume)</li>
                <li>MOQ — démarrer en dropship sans gros minimum, puis bulk ?</li>
                <li>Conditions de paiement — Net 30 possible ?</li>
                <li>Délais de livraison vers Val-d'Or (J9P)</li>
                <li>Packaging brandé / dropship aveugle (bordereaux Kaizen)</li>
                <li>Politique de retours / produits défectueux en dropship</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-zinc-200 text-base mb-2">🧭 Gestion de la rencontre (structure pro)</h3>
              <ol className="list-decimal list-inside space-y-1 text-zinc-300">
                <li><b>Ouverture (1 min)</b> : remercier, confirmer la durée dispo.</li>
                <li><b>Mon pitch (30 sec)</b> : qui je suis + pourquoi je suis là.</li>
                <li><b>Écouter (3-4 min)</b> : leur laisser présenter leur programme.</li>
                <li><b>Mes 7 questions</b> : noter les réponses.</li>
                <li><b>Prochaines étapes</b> : « Quelle est la meilleure façon d'ouvrir le compte ? » + qui fait quoi, et quand.</li>
                <li><b>Clôture</b> : remercier, résumer les actions, fixer le suivi.</li>
              </ol>
              <p className="text-xs text-zinc-500 mt-2">💡 Astuce : arrive avec ta liste courte de 1re commande (gants, gardes, protège-tibias, gis) et tes coordonnées (NEQ, adresses facturation + livraison J9P).</p>
            </section>

          </div>
        </DialogContent>
      </Dialog>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="b2b-counts">
        {data.statuses?.map((s) => (
          <div
            key={s}
            className={`p-4 rounded-xl border ${STATUS_LABELS[s].color}`}
            data-testid={`b2b-count-${s}`}
          >
            <div className="text-xs uppercase tracking-wider opacity-70">{STATUS_LABELS[s].emoji} {STATUS_LABELS[s].label}</div>
            <div className="text-2xl font-bold mt-1">{data.counts[s] || 0}</div>
          </div>
        ))}
      </div>

      {loading && <div className="text-zinc-500 text-sm">Chargement…</div>}

      {/* Kanban */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="b2b-kanban">
        {data.statuses?.map((s) => (
          <div
            key={s}
            className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 min-h-[200px]"
            data-testid={`b2b-column-${s}`}
          >
            <div className={`text-xs font-semibold uppercase tracking-wider px-2 py-1.5 mb-3 rounded ${STATUS_LABELS[s].color}`}>
              {STATUS_LABELS[s].emoji} {STATUS_LABELS[s].label} · {data.counts[s] || 0}
            </div>
            <div className="space-y-3">
              {(data.by_status?.[s] || []).map((sup) => (
                <SupplierCard
                  key={sup.id}
                  supplier={sup}
                  onMove={(newStatus) => updateStatus(sup.id, newStatus)}
                  onEdit={() => setEditing(sup)}
                  onDelete={() => removeSupplier(sup.id)}
                  onRelance={() => relanceOne(sup.id, sup.brand)}
                  onPublicityKit={() => openPublicityKit(sup.id, sup.brand)}
                  onDossier={() => openDossier(sup)}
                  allStatuses={data.statuses}
                />
              ))}
              {(data.by_status?.[s] || []).length === 0 && (
                <div className="text-xs text-zinc-600 italic px-2 py-4 text-center">— vide —</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Dossier courriel + analyse IA */}
      {dossier && (
        <Dialog open onOpenChange={(v) => !v && (setDossier(null), setAnalysis(null))}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="b2b-dossier-dialog">
            <DialogHeader>
              <DialogTitle>📬 Suivi courriel — {dossier.supplier.brand}</DialogTitle>
            </DialogHeader>
            <div className="text-xs text-zinc-400 mb-3">{dossier.supplier.contact_name || ""} · {dossier.supplier.contact_email}</div>

            {/* Aperçu rapide : où j'en suis (3 derniers envoyés + 3 derniers reçus) */}
            {!dossierLoading && !dossier.warning && ((dossier.last_sent || []).length > 0 || (dossier.last_received || []).length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="b2b-quickglance">
                <div className="bg-amber-950/20 border border-amber-900/40 rounded-xl p-3">
                  <div className="text-xs font-semibold text-amber-300 mb-2">📤 Mes 3 derniers ENVOYÉS</div>
                  {(dossier.last_sent || []).length === 0 ? (
                    <div className="text-zinc-600 text-xs">Aucun courriel envoyé.</div>
                  ) : (dossier.last_sent || []).map((m) => (
                    <div key={m.id} className="mb-2 last:mb-0" data-testid="b2b-last-sent-item">
                      <div className="text-zinc-200 text-xs font-medium truncate">{m.subject || "(sans objet)"}</div>
                      <div className="text-zinc-500 text-[10px]">{m.date}</div>
                    </div>
                  ))}
                </div>
                <div className="bg-sky-950/20 border border-sky-900/40 rounded-xl p-3">
                  <div className="text-xs font-semibold text-sky-300 mb-2">📥 Leurs 3 derniers REÇUS</div>
                  {(dossier.last_received || []).length === 0 ? (
                    <div className="text-zinc-600 text-xs">Aucune réponse reçue d'eux.</div>
                  ) : (dossier.last_received || []).map((m) => (
                    <div key={m.id} className="mb-2 last:mb-0" data-testid="b2b-last-received-item">
                      <div className="text-zinc-200 text-xs font-medium truncate">{m.subject || "(sans objet)"}</div>
                      <div className="text-zinc-500 text-[10px]">{m.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analyse IA */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-amber-300">🧠 Analyse de cohérence (IA)</span>
                <Button size="sm" onClick={() => runAnalysis(dossier.supplier)} disabled={analyzing} className="bg-amber-500 text-black hover:bg-amber-400 h-7 text-xs" data-testid="b2b-analyze-btn">
                  {analyzing ? "Analyse…" : analysis ? "Réanalyser" : "Analyser le dossier"}
                </Button>
              </div>
              {analysis ? (
                <div className="space-y-2 text-sm" data-testid="b2b-analysis-result">
                  <p className="text-zinc-300"><span className="text-zinc-500">Résumé :</span> {analysis.resume}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="bg-zinc-900 rounded-lg p-2"><span className="text-zinc-500 text-xs">Étape actuelle</span><div className="text-zinc-200">{analysis.etape_actuelle}</div></div>
                    <div className="bg-zinc-900 rounded-lg p-2"><span className="text-zinc-500 text-xs">En attente de</span><div className={`font-semibold ${analysis.en_attente_de === "MOI" ? "text-amber-300" : analysis.en_attente_de === "FOURNISSEUR" ? "text-sky-300" : "text-emerald-300"}`}>{analysis.en_attente_de}</div></div>
                  </div>
                  <div className="bg-emerald-950/40 border border-emerald-900 rounded-lg p-2">
                    <span className="text-emerald-400 text-xs font-semibold">👉 Prochaine action recommandée</span>
                    <div className="text-emerald-100">{analysis.prochaine_action_recommandee}</div>
                  </div>
                  {analysis.questions_en_suspens?.length > 0 && (
                    <div><span className="text-zinc-500 text-xs">Questions en suspens :</span><ul className="list-disc list-inside text-zinc-300">{analysis.questions_en_suspens.map((q, i) => <li key={i}>{q}</li>)}</ul></div>
                  )}
                  {analysis.points_a_ne_pas_oublier?.length > 0 && (
                    <div><span className="text-zinc-500 text-xs">À ne pas oublier :</span><ul className="list-disc list-inside text-zinc-300">{analysis.points_a_ne_pas_oublier.map((q, i) => <li key={i}>{q}</li>)}</ul></div>
                  )}
                  {analysis.drapeaux?.length > 0 && (
                    <div className="bg-red-950/40 border border-red-900 rounded-lg p-2"><span className="text-red-300 text-xs font-semibold">⚠️ Drapeaux</span><ul className="list-disc list-inside text-red-200">{analysis.drapeaux.map((q, i) => <li key={i}>{q}</li>)}</ul></div>
                  )}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">Clique « Analyser le dossier » pour que l'IA résume où tu en es, qui doit répondre, et ce qu'il ne faut pas oublier.</p>
              )}
            </div>

            {/* Timeline courriels */}
            <div className="text-sm font-semibold text-zinc-300 mb-2">
              Historique des courriels {dossier.messages ? `(${dossier.messages.length})` : ""}
              {dossier.counts && Object.keys(dossier.counts).length > 0 && (
                <span className="ml-2 text-xs text-zinc-500">{Object.entries(dossier.counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
              )}
            </div>
            {dossierLoading ? (
              <div className="text-zinc-500 text-sm py-6 text-center">Chargement des courriels…</div>
            ) : dossier.warning ? (
              <div className="text-amber-300 text-sm py-4">{dossier.warning}</div>
            ) : (dossier.messages || []).length === 0 ? (
              <div className="text-zinc-500 text-sm py-4">Aucun courriel trouvé avec ce contact.</div>
            ) : (
              <div className="space-y-2" data-testid="b2b-timeline">
                {dossier.messages.map((m) => (
                  <div key={m.id} className={`rounded-lg p-3 border text-sm ${m.direction === "out" ? "bg-amber-950/20 border-amber-900/40" : "bg-zinc-900 border-zinc-800"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs font-semibold ${m.direction === "out" ? "text-amber-300" : "text-sky-300"}`}>
                        {m.direction === "out" ? "→ Envoyé (toi)" : "← Reçu"} · {m.folder}
                      </span>
                      <span className="text-xs text-zinc-500">{m.date}</span>
                    </div>
                    <div className="text-zinc-200 font-medium mt-1">{m.subject || "(sans objet)"}</div>
                    <div className="text-zinc-500 text-xs mt-1 line-clamp-2">{m.snippet}</div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Edit dialog */}
      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-2xl" data-testid="b2b-edit-dialog">
            <DialogHeader>
              <DialogTitle>{editing.id ? "Modifier fournisseur" : "Nouveau fournisseur"}</DialogTitle>
            </DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4 py-2">
              <Field label="Marque *">
                <Input value={editing.brand || ""} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} data-testid="b2b-edit-brand" />
              </Field>
              <Field label="Type">
                <Select value={editing.kind || "wholesale"} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                  <SelectTrigger data-testid="b2b-edit-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(KIND_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Statut">
                <Select value={editing.status || "demande"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="b2b-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l.emoji} {l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Conditions / remise">
                <Input value={editing.discount_terms || ""} onChange={(e) => setEditing({ ...editing, discount_terms: e.target.value })} placeholder="ex: 50% off RRP" data-testid="b2b-edit-terms" />
              </Field>
              <Field label="Contact (nom)">
                <Input value={editing.contact_name || ""} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} data-testid="b2b-edit-contact-name" />
              </Field>
              <Field label="Contact (email)">
                <Input value={editing.contact_email || ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} data-testid="b2b-edit-contact-email" />
              </Field>
              <Field label="Contact (téléphone)">
                <Input value={editing.contact_phone || ""} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} data-testid="b2b-edit-contact-phone" />
              </Field>
              <Field label="URL portail / Calendly">
                <Input value={editing.portal_url || ""} onChange={(e) => setEditing({ ...editing, portal_url: e.target.value })} data-testid="b2b-edit-portal" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Prochaine action">
                  <Input value={editing.next_action || ""} onChange={(e) => setEditing({ ...editing, next_action: e.target.value })} data-testid="b2b-edit-next-action" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Notes">
                  <Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="b2b-edit-notes" />
                </Field>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button onClick={saveEdit} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="b2b-edit-save">Sauvegarder</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Broadcast SMS confirmation dialog */}
      {broadcastDialog && (
        <Dialog open onOpenChange={(v) => !v && setBroadcastDialog(null)}>
          <DialogContent className="max-w-2xl" data-testid="b2b-broadcast-dialog">
            <DialogHeader>
              <DialogTitle>📢 Diffuser SMS — {broadcastDialog.supplierBrand}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-red-950/40 border border-red-900 rounded-lg p-4">
                <div className="text-red-300 font-semibold mb-1">⚠️ ACTION IRRÉVERSIBLE</div>
                <div className="text-sm text-zinc-300">
                  Tu vas envoyer un vrai SMS Twilio à <strong className="text-amber-300">{broadcastPreview?.total || 0} membres actifs</strong>.
                  Chaque SMS coûte ~0.01 $ CAD. Total estimé : <strong className="text-amber-300">~{((broadcastPreview?.total || 0) * 0.01).toFixed(2)} $ CAD</strong>.
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-xs mb-1.5 block">Audience : Membres actifs (zp_status=STUDENT, téléphone validé)</Label>
                <div className="text-xs text-zinc-500 space-y-1">
                  {(broadcastPreview?.sample || []).map((s, i) => (
                    <div key={i}>· {s.name} ({s.phone_masked})</div>
                  ))}
                  {broadcastPreview?.total > 5 && (
                    <div className="italic text-zinc-600">… et {broadcastPreview.total - 5} autres</div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-xs mb-1.5 block">Message envoyé (avec opt-out auto)</Label>
                <Textarea
                  rows={6}
                  value={broadcastDialog.message + "\n\nRépondre STOP pour ne plus recevoir."}
                  onChange={(e) => setBroadcastDialog({ ...broadcastDialog, message: e.target.value.replace(/\n\nRépondre STOP.*$/s, "") })}
                  data-testid="b2b-broadcast-message"
                  className="font-mono text-xs"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBroadcastDialog(null); setBroadcastPreview(null); }} disabled={broadcasting}>
                Annuler
              </Button>
              <Button
                onClick={confirmBroadcast}
                disabled={broadcasting || !broadcastPreview?.total}
                className="bg-red-600 text-white hover:bg-red-500"
                data-testid="b2b-broadcast-confirm"
              >
                {broadcasting ? "Envoi en cours…" : `📢 Envoyer à ${broadcastPreview?.total || 0} membres`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Publicity kit dialog */}
      {kit && (
        <Dialog open onOpenChange={(v) => !v && setKit(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="b2b-publicity-dialog">
            <DialogHeader>
              <DialogTitle>📢 Kit publicité — {kit.brand}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex flex-wrap gap-2" data-testid="b2b-publicity-tabs">
                {Object.entries(kit.formats).map(([key, f]) => (
                  <button
                    key={key}
                    onClick={() => setKitTab(key)}
                    data-testid={`b2b-publicity-tab-${key}`}
                    className={
                      "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors " +
                      (kitTab === key
                        ? "bg-amber-500 text-black border-amber-500"
                        : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-amber-500/40")
                    }
                  >
                    {f.label} <span className="opacity-50">({f.char_count})</span>
                  </button>
                ))}
              </div>
              {kit.formats[kitTab]?.subject && (
                <div className="text-xs text-zinc-400">
                  <strong className="text-zinc-200">Sujet :</strong> {kit.formats[kitTab].subject}
                </div>
              )}
              <Textarea
                rows={20}
                value={kit.formats[kitTab]?.content || ""}
                readOnly
                data-testid="b2b-publicity-content"
                className="font-mono text-xs"
              />
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => copyToClipboard(kit.formats[kitTab].content)}
                  className="bg-amber-500 text-black hover:bg-amber-400"
                  data-testid="b2b-publicity-copy"
                >
                  📋 Copier dans le presse-papier
                </Button>
                {kitTab === "email" && kit.formats[kitTab]?.subject && (
                  <Button
                    onClick={() => copyToClipboard(`Sujet : ${kit.formats[kitTab].subject}\n\n${kit.formats[kitTab].content}`)}
                    variant="outline"
                  >
                    📋 Copier (sujet + corps)
                  </Button>
                )}
                {kitTab === "sms_members" && (
                  <Button
                    onClick={() => openBroadcastDialog(kit.formats.sms_members.content, kit.brand, kit.supplier_id)}
                    className="bg-red-600 text-white hover:bg-red-500"
                    data-testid="b2b-publicity-broadcast-sms"
                  >
                    📢 Diffuser SMS aux membres maintenant
                  </Button>
                )}
                <Button variant="outline" onClick={() => setKit(null)}>Fermer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-zinc-300 text-xs mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const SupplierCard = ({ supplier, onMove, onEdit, onDelete, onRelance, onPublicityKit, onDossier, allStatuses }) => {
  const s = supplier;
  const now = new Date();
  const lastEmail = s.last_email_at ? new Date(s.last_email_at) : null;
  const ageDays = lastEmail ? Math.floor((now - lastEmail) / (1000 * 60 * 60 * 24)) : null;
  const isSilent = ageDays !== null && ageDays >= 7 && ["demande", "repondu"].includes(s.status);

  return (
    <div
      className={
        "bg-black border rounded-lg p-3 transition-colors space-y-2 " +
        (isSilent ? "border-amber-500/60 shadow-[0_0_15px_-5px_rgba(212,175,55,0.4)]" : "border-zinc-800 hover:border-amber-500/40")
      }
      data-testid={`b2b-card-${s.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-white text-sm leading-tight">{s.brand}</div>
        <Badge variant="outline" className="shrink-0 text-[10px] border-zinc-700">{KIND_LABELS[s.kind] || s.kind}</Badge>
      </div>
      {isSilent && (
        <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1" data-testid={`b2b-card-${s.id}-silent-flag`}>
          ⏰ Silencieux depuis {ageDays}j — relance suggérée
        </div>
      )}
      {s.contact_name && (
        <div className="text-xs text-zinc-400">👤 {s.contact_name}</div>
      )}
      {s.contact_email && (
        <a href={`mailto:${s.contact_email}`} className="text-xs text-blue-400 hover:underline block truncate" data-testid={`b2b-card-${s.id}-email`}>
          ✉️ {s.contact_email}
        </a>
      )}
      {s.discount_terms && (
        <div className="text-xs text-emerald-400">💰 {s.discount_terms}</div>
      )}
      {s.next_action && (
        <div className="text-xs text-amber-300 leading-snug">→ {s.next_action}</div>
      )}
      {s.portal_url && (
        <a href={s.portal_url} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-blue-400 block truncate">
          🔗 {s.portal_url.replace(/^https?:\/\//, "").slice(0, 40)}
        </a>
      )}
      {s.last_email_at && (
        <div className="text-[10px] text-zinc-600">Dernier contact : {s.last_email_at.slice(0, 10)}</div>
      )}
      {s.last_relance_at && (
        <div className="text-[10px] text-amber-600/70">Dernière relance : {s.last_relance_at.slice(0, 10)}</div>
      )}
      <div className="flex gap-1 pt-1.5 border-t border-zinc-900 mt-2">
        <Select value={s.status} onValueChange={onMove}>
          <SelectTrigger className="h-7 text-xs flex-1" data-testid={`b2b-card-${s.id}-status-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {allStatuses.map((st) => (
              <SelectItem key={st} value={st}>{STATUS_LABELS[st].emoji} {STATUS_LABELS[st].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {s.contact_email && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-amber-300" onClick={onRelance} data-testid={`b2b-card-${s.id}-relance`} title="Générer brouillon de relance Gmail">⏰</Button>
        )}
        {["approuve", "actif"].includes(s.status) && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-emerald-300" onClick={onPublicityKit} data-testid={`b2b-card-${s.id}-publicity`} title="Kit publicité (FB/IG/TikTok/SMS/Email/Affiche)">📢</Button>
        )}
        {s.contact_email && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-sky-300" onClick={onDossier} data-testid={`b2b-card-${s.id}-dossier`} title="Suivi courriel + analyse IA du dossier">📬</Button>
        )}
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={onEdit} data-testid={`b2b-card-${s.id}-edit`}>✏️</Button>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-400" onClick={onDelete} data-testid={`b2b-card-${s.id}-delete`}>🗑️</Button>
      </div>
    </div>
  );
};
